import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";

import Box from "@mui/material/Box";
import CssBaseline from "@mui/material/CssBaseline";
import Divider from "@mui/material/Divider";
import Drawer from "@mui/material/Drawer";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Toolbar from "@mui/material/Toolbar";
import Tooltip from "@mui/material/Tooltip";
import Avatar from "@mui/material/Avatar";
import IconButton from "@mui/material/IconButton";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import type { SelectChangeEvent } from "@mui/material/Select";
import HomeIcon from "@mui/icons-material/Home";
import InstallMobileIcon from "@mui/icons-material/InstallMobile";
import LogoutIcon from "@mui/icons-material/Logout";
import SchoolIcon from "@mui/icons-material/School";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import Button from "@mui/material/Button";

import Header from "../app-bar/AppBar";
import { ParticifyLogo, TrakifyLogo, CalendFyLogo, NotifyLogo, LogifyLogo, ReportifyLogo } from "../../components/logos/ModuleLogos";
import UserSettingsDialog from "../../components/user-settings-dialog/UserSettingsDialog";

import { useAttendanceContext } from "../../contexts/AttendanceContext";
import { drawerWidth, drawerCollapsedWidth } from "../../constants/constants";
import { useTranslationsContext } from "../../contexts/TranslationsContext";
import { useUserContext } from "../../contexts/UserContext";
import { isMobileDevice } from "../../helpers";
import { listClassrooms } from "../../api/classrooms";
import { useRestQuery } from "../../hooks/useRestQuery";
import type { ClassRoomPublic } from "../../api/types";

import "./AppDrawer.css";

const STORAGE_KEY = "tutorify-drawer-collapsed";

export default function ResponsiveDrawer() {
  const { classId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const userContext = useUserContext();
  const attendance = useAttendanceContext();
  const { translation } = useTranslationsContext();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [logoutOpen, setLogoutOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState("/");
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem(STORAGE_KEY) === "true"; } catch { return false; }
  });

  const toggleCollapsed = useCallback(() => {
    setCollapsed((prev) => {
      const next = !prev;
      try { localStorage.setItem(STORAGE_KEY, String(next)); } catch { /* noop */ }
      return next;
    });
  }, []);

  const currentWidth = collapsed ? drawerCollapsedWidth : drawerWidth;

  // Sync the CSS variable so .outlet-app margin adapts to drawer width
  useEffect(() => {
    document.documentElement.style.setProperty("--drawer-left-space", `${currentWidth}px`);
  }, [currentWidth]);

  // Fetch classrooms so the selector always has data
  const { data: classRoomGroups } = useRestQuery(listClassrooms, {
    key: "/classrooms",
    deps: [userContext.token],
  });

  const allClassrooms: ClassRoomPublic[] = useMemo(
    () =>
      classRoomGroups?.reduce(
        (acc, g) => acc.concat(g.classRooms),
        [] as ClassRoomPublic[]
      ) ?? [],
    [classRoomGroups]
  );

  // Sync classrooms into attendance context (same as Home.tsx does)
  useEffect(() => {
    if (allClassrooms.length > 0) {
      attendance.setClassRooms(allClassrooms);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allClassrooms]);

  const isAttendance =
    selectedIndex !== "/" && selectedIndex.includes("/class-attendance/");
  const isTrackifyClass = selectedIndex.includes("/trackify/");
  const isCalendfy = selectedIndex.includes("/calendfy");
  const isNotify = selectedIndex.includes("/notify");
  const isLogify = selectedIndex.includes("/logify");
  const isReportify = selectedIndex.includes("/reportify");
  const isHome = selectedIndex === "/";
  const classIdParsed =
    classId !== undefined ? classId : attendance.classRoom?.id;

  // The "active class" for module shortcuts — either from URL or from context
  const activeClassId = classIdParsed || "";
  const hasActiveClass = Boolean(activeClassId);

  const activeModule: "home" | "particify" | "trackify" | "calendfy" | "logify" | "reportify" = isAttendance
    ? "particify"
    : isTrackifyClass
    ? "trackify"
    : isCalendfy
    ? "calendfy"
    : isLogify
    ? "logify"
    : isReportify
    ? "reportify"
    : "home";

  function handleDrawerToggle(): void {
    setMobileOpen(!mobileOpen);
  }

  function handleNavigationClick(isMobile: boolean): void {
    if (isMobile && mobileOpen) {
      setMobileOpen(false);
    }
    window.scrollTo(0, 0);
  }

  function getQualificationPageState() {
    const breadcrumbs = attendance?.breadcrumbs;
    const attendanceIndex = breadcrumbs?.findIndex(
      (item: any) =>
        !item?.label?.includes(translation.CLASS_ROOM.defaultMessage)
    );
    const sliceIndex =
      attendanceIndex > -1 ? attendanceIndex : breadcrumbs?.length;

    return {
      breadcrumbs: [...(breadcrumbs?.slice(0, sliceIndex) || [])],
    };
  }

  function handleLogOut() {
    setLogoutOpen(true);
  }

  function handleLogOutConfirm() {
    setLogoutOpen(false);
    userContext.logOut();
  }

  function handleAppInstallation(isMobile: boolean) {
    if (isMobile && mobileOpen) {
      setMobileOpen(false);
    }
    userContext.setAppInstalledViewed(false);
  }

  function handleClassSelect(e: SelectChangeEvent<string>) {
    const id = e.target.value;
    if (!id) return;
    const cr = allClassrooms.find((c) => String(c.id) === String(id));
    if (cr) {
      attendance.setClassRoom(cr);
      attendance.setQualificationDate?.(undefined);
      navigate(`/class/${cr.id}`, {
        state: {
          breadcrumbs: [
            { id: 0, label: translation.CLASS_ROOMS.defaultMessage, path: "/", icon: "HomeIcon" },
            { id: 1, label: `${translation.CLASS_ROOM.defaultMessage}: ${cr.name} - ${cr.subject}`, path: `/class/${cr.id}`, icon: "AppsIcon" },
          ],
        },
      });
    }
  }

  useEffect(() => {
    setSelectedIndex(location?.pathname || "/");
    // Clear breadcrumbs when navigating to Home
    if (location?.pathname === "/") {
      attendance.setBreadcrumbs([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // When collapsed, tooltips should always show (act like a rail)
  const shouldDisableTooltip = (isMobile: boolean) => isMobile && !collapsed ? true : !collapsed;

  const drawerContent = (isMobile: boolean) => (
    <div
      className="drawer-container"
      data-module={activeModule}
      data-collapsed={!isMobile && collapsed ? "true" : undefined}
    >
      <Toolbar className="drawer-header" />

      {/* User account */}
      <div className="drawer-account">
        <Tooltip
          title={collapsed && !isMobile
            ? (userContext.firstName || userContext.email?.split("@")[0] || "")
            : (translation.ACCOUNT_SETTINGS?.defaultMessage || "Account settings")}
          enterDelay={300}
          placement="right"
        >
          <Avatar
            className="drawer-account-avatar drawer-account-avatar-clickable"
            onClick={() => setSettingsOpen(true)}
          >
            {userContext.email?.slice(0, 1).toLocaleUpperCase()}
          </Avatar>
        </Tooltip>
        <div
          className="drawer-account-info drawer-account-info-clickable drawer-collapsible-text"
          onClick={() => setSettingsOpen(true)}
        >
          <span className="drawer-account-name">
            {userContext.firstName || userContext.email?.split("@")[0]}
          </span>
          <Tooltip title={userContext.email} enterDelay={400}>
            <span className="drawer-account-email">{userContext.email}</span>
          </Tooltip>
        </div>
        <Tooltip title={translation.LOGOUT?.defaultMessage || "Logout"}>
          <IconButton className="drawer-logout-btn drawer-collapsible-text" onClick={handleLogOut} size="small">
            <LogoutIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
      </div>

      <Divider sx={{ opacity: 0.06 }} />

      {/* Home link — always visible */}
      <List>
        <ListItem disablePadding>
          <Tooltip
            arrow
            title={translation.CLASS_ROOMS?.defaultMessage || "My Classes"}
            placement="right"
            disableHoverListener={shouldDisableTooltip(isMobile)}
          >
            <Link
              className="w-full"
              to="/"
              state={{ breadcrumbs: [] }}
            >
              <ListItemButton
                selected={isHome}
                onClick={() => handleNavigationClick(isMobile)}
              >
                <ListItemIcon className="drawer-icon-container">
                  <HomeIcon
                    color={isHome ? "primary" : "inherit"}
                  />
                </ListItemIcon>
                <ListItemText
                  className="drawer-collapsible-text"
                  primary={translation.CLASS_ROOMS?.defaultMessage || "My Classes"}
                  primaryTypographyProps={{
                    color: isHome ? "primary" : "inherit",
                    fontWeight: isHome ? 700 : 400,
                    fontSize: "0.88rem",
                  }}
                />
              </ListItemButton>
            </Link>
          </Tooltip>
        </ListItem>
      </List>

      <Divider sx={{ opacity: 0.06 }} />

      {/* ── Classroom selector — hidden when collapsed ── */}
      <div className="drawer-collapsible-section">
        {allClassrooms.length > 0 && (
          <div className="drawer-class-selector">
            <div className="drawer-class-selector-label">
              <SchoolIcon sx={{ fontSize: 13, opacity: 0.5 }} />
              <span>{translation.CLASS_ROOM?.defaultMessage || "Class"}</span>
            </div>
            <Select
              value={String(activeClassId)}
              onChange={handleClassSelect}
              size="small"
              displayEmpty
              fullWidth
              className="drawer-class-select"
              MenuProps={{
                PaperProps: {
                  sx: {
                    backgroundColor: "#2e2a45",
                    backgroundImage: "none",
                    border: "1px solid rgba(255,255,255,0.08)",
                    borderRadius: "8px",
                    maxHeight: 280,
                    "& .MuiMenuItem-root": {
                      fontSize: "0.78rem",
                      color: "rgba(255,255,255,0.7)",
                      "&:hover": { backgroundColor: "rgba(122,172,179,0.1)" },
                      "&.Mui-selected": { backgroundColor: "rgba(122,172,179,0.15)", color: "#7aacb3" },
                    },
                  },
                },
              }}
              sx={{
                fontSize: "0.78rem",
                color: "rgba(255,255,255,0.8)",
                backgroundColor: "rgba(255,255,255,0.04)",
                borderRadius: "6px",
                "& .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(255,255,255,0.08)" },
                "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(255,255,255,0.15)" },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#7aacb3" },
                "& .MuiSelect-icon": { color: "rgba(255,255,255,0.3)" },
                "& .MuiSelect-select": { py: "6px" },
              }}
            >
              <MenuItem value="" disabled>
                <em style={{ opacity: 0.5 }}>{translation.CALENDFY_NO_CLASS?.defaultMessage || "Select class..."}</em>
              </MenuItem>
              {allClassrooms.map((cr) => (
                <MenuItem key={cr.id} value={String(cr.id)}>
                  {cr.name} {cr.subject && <span style={{ opacity: 0.45 }}>&nbsp;· {cr.subject}</span>}
                </MenuItem>
              ))}
            </Select>
          </div>
        )}
      </div>

      {/* ── Module shortcuts — always visible ── */}
      <div className="drawer-modules-section">
        <div className="drawer-modules-label drawer-collapsible-text">
          {translation.MODULES?.defaultMessage || "Modules"}
        </div>
        <List disablePadding>
          {/* ParticiFy */}
          <ListItem disablePadding>
            <Tooltip
              arrow
              title={hasActiveClass ? "ParticiFy" : (translation.CALENDFY_NO_CLASS?.defaultMessage || "Select a class first")}
              placement="right"
              disableHoverListener={hasActiveClass && shouldDisableTooltip(isMobile)}
            >
              <span className="w-full">
                {hasActiveClass ? (
                  <Link
                    className="w-full"
                    to={`/class-attendance/${activeClassId}`}
                    state={getQualificationPageState()}
                  >
                    <ListItemButton
                      selected={isAttendance}
                      onClick={() => {
                        const cr = allClassrooms.find((c) => String(c.id) === String(activeClassId));
                        if (cr) {
                          attendance.setClassRoom(cr);
                          attendance.setQualificationDate?.(undefined);
                        }
                        handleNavigationClick(isMobile);
                      }}
                    >
                      <ListItemIcon className="drawer-icon-container">
                        <div className="drawer-module-icon-wrap">
                          <ParticifyLogo size={20} />
                        </div>
                      </ListItemIcon>
                      <ListItemText
                        className="drawer-collapsible-text"
                        primary="ParticiFy"
                        primaryTypographyProps={{
                          color: isAttendance ? "primary" : "inherit",
                          fontWeight: isAttendance ? 600 : 400,
                          fontSize: "0.84rem",
                        }}
                      />
                      {isAttendance && <span className="drawer-nav-dot" data-active="true" />}
                    </ListItemButton>
                  </Link>
                ) : (
                  <ListItemButton disabled className="drawer-module-disabled">
                    <ListItemIcon className="drawer-icon-container">
                      <div className="drawer-module-icon-wrap" style={{ opacity: 0.35 }}>
                        <ParticifyLogo size={20} />
                      </div>
                    </ListItemIcon>
                    <ListItemText
                      className="drawer-collapsible-text"
                      primary="ParticiFy"
                      primaryTypographyProps={{ fontSize: "0.84rem" }}
                    />
                  </ListItemButton>
                )}
              </span>
            </Tooltip>
          </ListItem>

          {/* TrackiFy */}
          <ListItem disablePadding>
            <Tooltip
              arrow
              title={hasActiveClass ? "TrackiFy" : (translation.CALENDFY_NO_CLASS?.defaultMessage || "Select a class first")}
              placement="right"
              disableHoverListener={hasActiveClass && shouldDisableTooltip(isMobile)}
            >
              <span className="w-full">
                {hasActiveClass ? (
                  <Link
                    className="w-full"
                    to={`/trackify/${activeClassId}`}
                  >
                    <ListItemButton
                      selected={isTrackifyClass}
                      onClick={() => {
                        const cr = allClassrooms.find((c) => String(c.id) === String(activeClassId));
                        if (cr) {
                          attendance.setClassRoom(cr);
                        }
                        handleNavigationClick(isMobile);
                      }}
                    >
                      <ListItemIcon className="drawer-icon-container">
                        <div className="drawer-module-icon-wrap">
                          <TrakifyLogo size={20} />
                        </div>
                      </ListItemIcon>
                      <ListItemText
                        className="drawer-collapsible-text"
                        primary="TrackiFy"
                        primaryTypographyProps={{
                          color: isTrackifyClass ? "primary" : "inherit",
                          fontWeight: isTrackifyClass ? 600 : 400,
                          fontSize: "0.84rem",
                        }}
                      />
                      {isTrackifyClass && (
                        <span className="drawer-nav-dot" data-active="true" data-accent="rose" />
                      )}
                    </ListItemButton>
                  </Link>
                ) : (
                  <ListItemButton disabled className="drawer-module-disabled">
                    <ListItemIcon className="drawer-icon-container">
                      <div className="drawer-module-icon-wrap" style={{ opacity: 0.35 }}>
                        <TrakifyLogo size={20} />
                      </div>
                    </ListItemIcon>
                    <ListItemText
                      className="drawer-collapsible-text"
                      primary="TrackiFy"
                      primaryTypographyProps={{ fontSize: "0.84rem" }}
                    />
                  </ListItemButton>
                )}
              </span>
            </Tooltip>
          </ListItem>

          {/* ReportiFy — always enabled */}
          <ListItem disablePadding>
            <Tooltip
              arrow
              title={translation.REPORTIFY?.defaultMessage || "Reports"}
              placement="right"
              disableHoverListener={shouldDisableTooltip(isMobile)}
            >
              <Link className="w-full" to="/reportify">
                <ListItemButton
                  selected={isReportify}
                  onClick={() => handleNavigationClick(isMobile)}
                >
                  <ListItemIcon className="drawer-icon-container">
                    <div className="drawer-module-icon-wrap">
                      <ReportifyLogo size={20} />
                    </div>
                  </ListItemIcon>
                  <ListItemText
                    className="drawer-collapsible-text"
                    primary="ReportiFy"
                    primaryTypographyProps={{
                      color: isReportify ? "primary" : "inherit",
                      fontWeight: isReportify ? 600 : 400,
                      fontSize: "0.84rem",
                    }}
                  />
                  {isReportify && <span className="drawer-nav-dot" data-active="true" />}
                </ListItemButton>
              </Link>
            </Tooltip>
          </ListItem>

          {/* LogiFy — always enabled */}
          <ListItem disablePadding>
            <Tooltip
              arrow
              title={translation.LOGIFY?.defaultMessage || "Observations"}
              placement="right"
              disableHoverListener={shouldDisableTooltip(isMobile)}
            >
              <Link className="w-full" to="/logify">
                <ListItemButton
                  selected={isLogify}
                  onClick={() => handleNavigationClick(isMobile)}
                >
                  <ListItemIcon className="drawer-icon-container">
                    <div className="drawer-module-icon-wrap">
                      <LogifyLogo size={20} />
                    </div>
                  </ListItemIcon>
                  <ListItemText
                    className="drawer-collapsible-text"
                    primary="LogiFy"
                    primaryTypographyProps={{
                      color: isLogify ? "primary" : "inherit",
                      fontWeight: isLogify ? 600 : 400,
                      fontSize: "0.84rem",
                    }}
                  />
                  {isLogify && <span className="drawer-nav-dot" data-active="true" data-accent="purple" />}
                </ListItemButton>
              </Link>
            </Tooltip>
          </ListItem>

          {/* CalendFy — always enabled */}
          <ListItem disablePadding>
            <Tooltip
              arrow
              title={translation.CALENDFY?.defaultMessage || "Calendar"}
              placement="right"
              disableHoverListener={shouldDisableTooltip(isMobile)}
            >
              <Link className="w-full" to="/calendfy">
                <ListItemButton
                  selected={isCalendfy}
                  onClick={() => handleNavigationClick(isMobile)}
                >
                  <ListItemIcon className="drawer-icon-container">
                    <div className="drawer-module-icon-wrap">
                      <CalendFyLogo size={20} />
                    </div>
                  </ListItemIcon>
                  <ListItemText
                    className="drawer-collapsible-text"
                    primary="CalendiFy"
                    primaryTypographyProps={{
                      color: isCalendfy ? "primary" : "inherit",
                      fontWeight: isCalendfy ? 600 : 400,
                      fontSize: "0.84rem",
                    }}
                  />
                  {isCalendfy && <span className="drawer-nav-dot" data-active="true" />}
                </ListItemButton>
              </Link>
            </Tooltip>
          </ListItem>

          {/* NotiFy — always enabled */}
          <ListItem disablePadding>
            <Tooltip
              arrow
              title={translation.NOTIFY_TITLE?.defaultMessage || "Notifications"}
              placement="right"
              disableHoverListener={shouldDisableTooltip(isMobile)}
            >
              <Link className="w-full" to="/notify/settings">
                <ListItemButton
                  selected={isNotify}
                  onClick={() => handleNavigationClick(isMobile)}
                >
                  <ListItemIcon className="drawer-icon-container">
                    <div className="drawer-module-icon-wrap">
                      <NotifyLogo size={20} />
                    </div>
                  </ListItemIcon>
                  <ListItemText
                    className="drawer-collapsible-text"
                    primary="NotiFy"
                    primaryTypographyProps={{
                      color: isNotify ? "primary" : "inherit",
                      fontWeight: isNotify ? 600 : 400,
                      fontSize: "0.84rem",
                    }}
                  />
                  {isNotify && <span className="drawer-nav-dot" data-active="true" />}
                </ListItemButton>
              </Link>
            </Tooltip>
          </ListItem>
        </List>
      </div>

      {/* Spacer */}
      <div className="drawer-spacer" />

      {/* ── Install App — mobile only ── */}
      {isMobileDevice() && (
        <>
          <Divider sx={{ opacity: 0.06 }} />
          <List>
            <ListItem disablePadding>
              <Tooltip
                arrow
                title={translation.APP_INSTALL.defaultMessage}
                placement="right"
                disableHoverListener={isMobile}
              >
                <ListItemButton
                  onClick={() => handleAppInstallation(isMobile)}
                >
                  <ListItemIcon className="drawer-icon-container">
                    <InstallMobileIcon color="secondary" />
                  </ListItemIcon>
                  <ListItemText
                    primary={translation.APP_INSTALL.defaultMessage}
                    primaryTypographyProps={{
                      color: "secondary",
                      fontSize: "0.85rem",
                    }}
                  />
                </ListItemButton>
              </Tooltip>
            </ListItem>
          </List>
        </>
      )}

      {/* Footer + collapse toggle */}
      <div className="drawer-footer">
        <div className="drawer-footer-gradient" />
        <div className="drawer-footer-row">
          <svg className="drawer-footer-logo" width="18" height="18" viewBox="0 0 50 50" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect width="50" height="50" rx="12" fill="#7aacb3" />
            <path d="M25 10L7 19l18 9 18-9-18-9z" fill="white" opacity="0.9" />
            <path d="M13 24v9c0 3.3 5.4 6 12 6s12-2.7 12-6v-9L25 30 13 24z" fill="white" opacity="0.6" />
          </svg>
          <span className="drawer-footer-mark drawer-collapsible-text">TutoriFy</span>
        </div>
        <span className="drawer-footer-tagline drawer-collapsible-text">
          Made with <span className="drawer-footer-heart">{"\u2665"}</span> for educators
        </span>

        {/* ── Collapse toggle — desktop only ── */}
        {!isMobile && (
          <Tooltip
            title={collapsed ? (translation.DRAWER_EXPAND?.defaultMessage || "Expand") : (translation.DRAWER_COLLAPSE?.defaultMessage || "Collapse")}
            placement="right"
            arrow
          >
            <button
              onClick={toggleCollapsed}
              className="drawer-collapse-btn"
              aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
            >
              {collapsed
                ? <ChevronRightIcon sx={{ fontSize: 16 }} />
                : <ChevronLeftIcon sx={{ fontSize: 16 }} />
              }
            </button>
          </Tooltip>
        )}
      </div>
    </div>
  );

  return (
    <Box sx={{ display: "flex" }}>
      <CssBaseline />
      <Header onToggle={handleDrawerToggle} activeModule={activeModule} currentDrawerWidth={currentWidth} />
      <Box
        component="nav"
        sx={{
          width: { md: currentWidth },
          flexShrink: { sm: 0 },
          transition: "width 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        }}
        aria-label="navigation"
      >
        {/* Mobile drawer — temporary (always full width) */}
        <Drawer
          container={window.document.body}
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: "block", md: "none" },
            "& .MuiDrawer-paper": {
              boxSizing: "border-box",
              width: drawerWidth,
            },
          }}
        >
          {drawerContent(true)}
        </Drawer>

        {/* Desktop drawer — permanent, collapsible */}
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: "none", md: "block" },
            "& .MuiDrawer-paper": {
              boxSizing: "border-box",
              width: currentWidth,
              transition: "width 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
              overflowX: "hidden",
            },
          }}
          open
        >
          {drawerContent(false)}
        </Drawer>
      </Box>
      <UserSettingsDialog open={settingsOpen} onClose={() => setSettingsOpen(false)} />

      {/* Logout confirmation */}
      <Dialog
        open={logoutOpen}
        onClose={() => setLogoutOpen(false)}
        aria-labelledby="logout-dialog-title"
        PaperProps={{
          sx: {
            borderRadius: "14px",
            background: "linear-gradient(145deg, #342f50 0%, #2a2644 100%)",
            border: "1px solid rgba(255,255,255,0.06)",
            minWidth: 320,
            overflow: "hidden",
            position: "relative",
            "&::before": {
              content: '""',
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              height: "2px",
              background: "linear-gradient(90deg, transparent, rgba(207,141,168,0.5), transparent)",
            },
          },
        }}
      >
        <DialogContent sx={{ pt: 3.5, pb: 1.5, px: 3, textAlign: "center" }}>
          <LogoutIcon sx={{ fontSize: 36, color: "#cf8da8", mb: 1.5, opacity: 0.85 }} />
          <DialogContentText
            id="logout-dialog-title"
            sx={{ color: "#e0ddf0", fontWeight: 700, fontSize: "1.05rem", mb: 1 }}
          >
            {translation.LOGOUT_CONFIRM_TITLE?.defaultMessage || "Log out?"}
          </DialogContentText>
          <DialogContentText sx={{ color: "#8a86a0", fontSize: "0.82rem", lineHeight: 1.5 }}>
            {translation.LOGOUT_CONFIRM_BODY?.defaultMessage || "You will need to sign in again to access your classes."}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ justifyContent: "center", gap: 1, pb: 3, px: 3 }}>
          <Button
            variant="outlined"
            onClick={() => setLogoutOpen(false)}
            sx={{
              borderRadius: "10px",
              borderColor: "rgba(255,255,255,0.1)",
              color: "#b0adc0",
              textTransform: "none",
              fontWeight: 600,
              fontSize: "0.82rem",
              px: 3,
              "&:hover": { borderColor: "rgba(255,255,255,0.2)", background: "rgba(255,255,255,0.04)" },
            }}
          >
            {translation.CANCEL?.defaultMessage || "Cancel"}
          </Button>
          <Button
            variant="contained"
            onClick={handleLogOutConfirm}
            sx={{
              borderRadius: "10px",
              background: "linear-gradient(135deg, #cf8da8 0%, #b87590 100%)",
              color: "#fff",
              textTransform: "none",
              fontWeight: 600,
              fontSize: "0.82rem",
              px: 3,
              boxShadow: "0 4px 16px rgba(207,141,168,0.25)",
              "&:hover": { background: "linear-gradient(135deg, #d99ab5 0%, #cf8da8 100%)" },
            }}
          >
            {translation.LOGOUT?.defaultMessage || "Log out"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
